import { Component, inject, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { Form, FormField } from '../interfaces/form.interface';
import { DropdownData, FilteredValue, InputType } from '../interfaces/input.interface';
import { NavigationService } from 'src/app/service/navigation.service';

@Component({
    selector: 'app-field',
    standalone: false,
    templateUrl: './field.component.html',
    styleUrls: ['./field.component.scss'],
})
export class FieldComponent<T> {
    @Input() field!: FormField<T>;

    @Input() model!: FormGroup;

    @Input() form!: Form<T>;

    @Input() originalModel: Record<string, string> = {};

    @Input() filterInputParcelas!: boolean;

    @Input() undoEnabled = false;

    INPUT = InputType;

    navigationService = inject(NavigationService);

    get filteredOptions(): FilteredValue<T>[] {
        return (this.field?.input?.data as DropdownData<T>).filtered ?? [];
    }

    public keyFilterFor(field: FormField<T>): RegExp | 'int' | null | undefined {

      // 1) Si el camp porta un keyFilter explícit, respecta'l
      const cfg = field.input?.textConfiguration;
      const explicit = cfg?.regex;
        
      if (((field.input?.textConfiguration?.regex?.test('1')) ?? false) && 
                    (Boolean((this.model.get(field.name)?.value))) && 
                    this.model.get(field.name)?.value !== '' &&
                    !isNaN(Number(this.model.get(field.name)?.value))
      ) {
        return 'int';
      } else {
        return explicit;
      }
    }

    public isFieldEdited(field: FormField<T>): boolean {

        const originalValue = this.originalModel[field.name ?? ''];

        const currentValue = this.model.get(field.name ?? '')?.value;
        
        return (field.conditions.hasUndoEnabled || this.undoEnabled) && currentValue !== originalValue;
    }

    public getEditedClass(field: FormField<T>): string {
        return this.isFieldEdited(field) ? 'edited' : '';
    }

    public isEditable(): boolean {
        return (this.form.type <= 2 || this.form.type === 6) && this.field.input.isEditable !== false;
    }

    public hasTwoFieldConcat(field: FormField<T>): boolean {
        const cfg = field.input.twoFieldsConcatConfiguration;

        return !!cfg
            && !!this.model.get(cfg.subField1)
            && !!this.model.get(cfg.subField2);
    }


    public sum(field: FormField<T>): string {
        if (this.form.type < 3) {
            const selectedItems = ((field.input.data as DropdownData<T>)?.selected ?? []) as Record<string, string>[];

            const total = selectedItems.reduce(
                (sum, value) => sum + parseFloat(
                    value?.[field.input?.dropdownConfiguration?.summation?.summand ?? '0']?.replace(',', '.') || '0.00'
                ),
                0
            );

            // Si existe función format la usamos, si no mostramos el total tal cual
             
            const formatter = (field.input?.dropdownConfiguration?.summation as any)?.format;
            const totalFormatted = typeof formatter === 'function' ? formatter(total) : total.toFixed(2);

            return `(${totalFormatted} ${field.input?.dropdownConfiguration?.summation?.uds})`;
        }
        return '';
    }


    public undoField(field: FormField<T>): void {
        const originalValue = this.originalModel[field.name ?? ''];

        this.model.patchValue({ [field.name ?? '']: originalValue });
        this.applySelected(field, originalValue);
    }

    public getAudio(field: string | number, event: string): void {
        this.model.patchValue({ [field]: event });
    }

    public updateModel(field: FormField<T>): void {
        if (field.input?.selectButtonConfiguration?.onClick) {
            const onClick: Promise<object> = field.input.selectButtonConfiguration.onClick(field as T);


            onClick.then((data) => {
                Object.entries(data).forEach((element) => {
                    this.model.patchValue({ [element[0]]: element[1] });
                });
                return;
            }).catch((error: string) => {
                console.log(error);
            });
        }
    }

    public updateImageModel(event: string, fieldName: string): void {
        this.model.patchValue({ [fieldName]: event });
    }

    private applySelected(field: FormField<T>, value: string | undefined): void {
        if (field.input.data) {
            (field.input.data as DropdownData<T>).selected = value as T;
        }
    }
}
